<script setup>
import { Icon } from '@iconify/vue2'
import { onClickOutside } from '@vueuse/core'
import { computed, ref, toRefs, watch } from 'vue'
import Checkbox from '../checkbox/Checkbox.vue'
import DropdownColunas from './DropdownColunas.vue'
const emit = defineEmits(['edit', 'update:checked', 'sortOptions'])

// interface Coluna {
//   nome: string
//   key: string
//   component?: Component,
//   enableSort: Boolean
// }

// interface BaseTableProps {
//   data: T[],
//   colunas: Coluna[],
//   displayMode?: Boolean
// }
const props = defineProps({
	data: { type: Array, required: true },
	colunas: { type: Array, required: true },
	displayMode: { type: Boolean, default: false },
	checked: { type: Array, default: () => [] },
})

watch(
	() => props.checked,
	(value) => {
		emit('update:checked', value)
	},
)

const { data } = toRefs(props)

// const rowsChecked = defineModel<string[]>('checked', { default: [] })
const checkAll = ref(false)

watch(checkAll, (value) => {
	if (value) {
		props.checked = data.value.map((dado) => dado.id)
	} else {
		props.checked = []
	}
})

const allColumnsKeys = computed(() => {
	return props.colunas.map((d) => d.key)
})

const columnsEnabled = ref(allColumnsKeys.value)

// const sortBy = defineModel('sortOptions', { default: [] })
const sortBy = ref([])
watch(sortBy, (value) => {
	emit('sortOptions', value)
})

function handleSort(key) {
	const index = sortBy.value.indexOf(key)

	if (index >= 0) {
		sortBy.value.splice(index, 1)
	} else {
		sortBy.value.push(key)
	}
}

const dropdownColunas = ref()
const openColunas = ref(false)

onClickOutside(dropdownColunas, () => {
	openColunas.value = false
})
</script>

<template>
	<table class="w-full">
		<thead class="w-full">
			<tr class="text-bold text-[#01395E] w-full">
				<th
					v-if="!displayMode"
					class="w-[2%] p-1 bg-blue-tint-3 text-start"
				>
					<Checkbox v-model="checkAll" />
				</th>
				<th
					v-for="coluna in colunas.filter((f) => columnsEnabled.includes(f.key))"
					:key="coluna.key"
					class="p-1 bg-blue-tint-3 text-start"
				>
					<span class="flex items-center justify-between text-clip">
						{{ coluna.nome }}
						<Icon
							v-if="coluna.enableSort"
							icon="lucide:sort-desc"
							class="text-blue-tint-4 text-4 cursor-pointer"
							@click="handleSort(coluna.key)"
						/>
					</span>
				</th>
				<th class="p-1 bg-blue-tint-3 text-start relative">
					<Icon
						icon="charm:menu-kebab"
						class="cursor-pointer"
						@click="openColunas = true"
					/>
					<div class="absolute z-10 -translate-x-full -translate-y-1\5">
						<DropdownColunas
							v-if="openColunas"
							ref="dropdownColunas"
							v-model="columnsEnabled"
							class="min-w-max"
							:options="
								colunas.map((d) => {
									return { label: d.nome, value: d.key }
								})
							"
						/>
					</div>
				</th>
			</tr>
		</thead>
		<tbody class="border-2 border-gray-light">
			<tr
				v-for="dado in data"
				:key="dado.id"
				class="even:bg-[#F1F5F8]"
				:class="checked.includes(dado.id) ? '!bg-blue-tint-4' : ''"
			>
				<td
					v-if="!displayMode"
					class="p-1 border-b-2 border-b-gray-light"
				>
					<Checkbox
						v-model="checked"
						:model-value="dado.id"
					/>
				</td>

				<td
					v-for="coluna in colunas.filter((f) => columnsEnabled.includes(f.key))"
					:key="coluna.key"
					:title="dado[coluna.key]"
					class="border-b-2 border-b-gray-light max-w-xs text-ellipsis overflow-hidden whitespace-nowrap"
				>
					<div
						v-if="coluna.component"
						:key="dado.id"
					>
						<Component
							:is="coluna.component"
							v-bind="{ ...dado, text: dado[coluna.key] }"
						/>
					</div>
					<div
						v-else-if="typeof dado[coluna.key] === 'object'"
						class="flex gap-2"
					>
						<span
							v-for="elemento in [...new Set(dado[coluna.key])]"
							:key="elemento"
							class="border font-bold text-[14px] text-[#737576] rounded-full px-2 border-gray-light"
						>
							{{ elemento }}</span
						>
					</div>
					<span v-else> {{ dado[coluna.key] }}</span>
				</td>
				<td class="border-b-2 border-b-gray-light">
					<slot
						v-if="!displayMode"
						name="custom-edit"
						v-bind="dado"
					>
						<Icon
							class="cursor-pointer"
							icon="fa-solid:edit"
							@click="emit('edit', dado.id)"
						/>
					</slot>
				</td>
			</tr>
		</tbody>
	</table>
</template>

<style scoped>
table thead tr th:nth-child(1) {
	border-top-left-radius: 5px;
}

table thead tr th:last-child {
	border-top-right-radius: 5px;
}

table tbody tr td {
	@apply py-1;
}
</style>
